import React, { useState } from 'react';

import { Flex } from '@chakra-ui/react';
import { FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import { openRealignModalAction } from 'components/RealignModal/redux/actions';
import { OBJECTIVE_GOAL_CREATION_MODAL_CHOICES } from 'components/TreeView';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch, useSelector } from 'react-redux';
import { closeKRPromptModal } from 'reducers/keyResultPromptModal';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { selectOrgData } from 'selectors/orgData';
import styled from 'styled-components';
import { Button } from 'styles/admin';
import { Divider1, SpaceBetweenContainer, StyledBox, VerticallyAlignedFlexContainer } from 'styles/common';
import { checkUserIsGoalAdmin } from 'utils/helpers/rolesAccess';

import { OBJECTIVE_ACCESS_PERMISSIONS } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { findAndReplaceString, getTimePeriodIdString } from 'constants/helper';
import { CrossIcon } from 'constants/icons';
import theme from 'constants/theme';

import { cloneObjective, deleteObjective } from 'services/objective';
import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';

import { Typography13, Typography14, Typography4, Typography6 } from 'uiToolkit/Typography';

import { TimePeriodDropDown } from './TagsList';

const Container = styled.div`
  display: flex;
  height: 56px;
  justify-content: flex-end;
  padding-bottom: 16px;
`;

const StyledButton = styled(Button)`
  height: ${(props) => props.height};
`;

const ModalWrapperContainer = styled.div`
  padding: 32px 24px 0 24px;
`;

const StyledSpaceBetweenContainer = styled(SpaceBetweenContainer)`
  align-items: center;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  .MuiFormControlLabel-label {
    font-size: 14px;
  }
`;

const StyledRadioGroup = styled(RadioGroup)`
  &.MuiFormGroup-root {
    justify-content: space-between;
  }
`;

export const ActionButtons = ({
  onSave,
  onCancel,
  loading,
  disabled,
  primaryActionText,
  showPrimaryAction = true,
  secondaryActionText,
  leftComponent = null,
  containerHeight = 'auto',
  buttonHeight = '40px',
}) => (
  <Container>
    <StyledBox flexcontainer>{leftComponent || null}</StyledBox>
    <StyledBox flexcontainer height={containerHeight}>
      {onCancel && (
        <StyledButton
          blueOutline
          size="large"
          onClick={onCancel}
          disabled={loading}
          style={{ width: '160px' }}
          height={buttonHeight}
        >
          {secondaryActionText || 'Cancel'}
        </StyledButton>
      )}
      {showPrimaryAction && (
        <>
          {onSave && (
            <StyledButton
              blue
              size="large"
              onClick={onSave}
              disabled={loading || disabled}
              style={{ marginLeft: onSave && onCancel ? '1.5rem' : '0', width: '160px' }}
              height={buttonHeight}
            >
              {primaryActionText || 'Save'}
            </StyledButton>
          )}
        </>
      )}
    </StyledBox>
  </Container>
);

// export const EditObjectiveModalView = ({ onSelect }) => {
//   const [selectedValue, setSelectedValue] = useState('yes');

//   const onSaveHandler = () => {
//     onSelect(selectedValue === 'yes');
//   };
//   const onDiscardHandler = () => onSelect(null);

//   const handleChange = event => {
//     setSelectedValue(event.target.value);
//   };
//   return (
//     <ModalWrapperContainer>
//       <Typography6 weight={400} bottomGutter={1}>
//         Edit Objective
//       </Typography6>

//       <Divider1 topGutter={3} bottomGutter={2} />

//       <StyledSpaceBetweenContainer>
//         <Typography4>Save objective across all other instances?</Typography4>

//         <Width160Container>
//           <StyledRadioGroup
//             row
//             aria-label="radio-selection"
//             name="prompt"
//             value={selectedValue}
//             onChange={handleChange}
//           >
//             <StyledFormControlLabel
//               value="yes"
//               control={<Radio color="primary" size="small" />}
//               label="Yes"
//               labelPlacement="End"
//             />
//             <StyledFormControlLabel
//               value="no"
//               control={<Radio color="primary" size="small" />}
//               label="No"
//               labelPlacement="End"
//             />
//           </StyledRadioGroup>
//         </Width160Container>
//       </StyledSpaceBetweenContainer>
//       <Divider1 topGutter={2} bottomGutter={2} />

//       <ActionButtons
//         onSave={onSaveHandler}
//         onCancel={onDiscardHandler}
//         primaryActionText="Save"
//         secondaryActionText="Discard"
//       />
//     </ModalWrapperContainer>
//   );
// };

const DEFAULT_CONFIRMATION_TEXT = 'Changes in timeline may affect existing objectives. Do you wish to proceed?';

export const EditObjectiveSettingsModalView = ({ onSelect, confirmationText = DEFAULT_CONFIRMATION_TEXT }) => {
  const onSaveHandler = () => {
    onSelect(true);
  };
  const onDiscardHandler = () => onSelect(false);

  return (
    <ModalWrapperContainer>
      <StyledSpaceBetweenContainer>
        <div>
          <Typography4>{confirmationText}</Typography4>
        </div>
      </StyledSpaceBetweenContainer>
      <Divider1 topGutter={2} bottomGutter={2} />

      <div style={{ paddingBottom: '16px' }}>
        <ActionButtons
          onSave={onSaveHandler}
          onCancel={onDiscardHandler}
          primaryActionText="Save"
          secondaryActionText="Discard"
        />
      </div>
    </ModalWrapperContainer>
  );
};

const Width160Container = styled.div`
  width: 160px;
`;

export const CloneObjectiveModalView = ({
  sourceObjective,
  timePeriodsList = [],
  timePeriodData = {},
  onCompletion,
}) => {
  const dispatch = useDispatch();
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const [currentObjective, setCurrentObjective] = useState({ ...sourceObjective });

  const [selectedTargetTimeBoundPeriod, setSelectedTargetTimeBoundPeriod] = useState(
    () =>
      timePeriodsList.filter(
        (timeBound) => getTimePeriodIdString(timeBound) !== getTimePeriodIdString(sourceObjective.timeBoundDetails)
      )?.[0] || null
  );

  const [includeObjectiveTree, setIncludeObjectiveTree] = useState('yes');

  const onDiscardHandler = () => onCompletion();

  const handleCloneObjective = () => {
    cloneObjective({
      tag: { ...currentObjective, timeBoundDetails: selectedTargetTimeBoundPeriod },
      includeObjectiveTree: includeObjectiveTree === 'yes',
    })
      .then((res) => {
        dispatch(
          showSuccessSnackbar(
            `Successfully cloned ${modifyString(
              objectivesName,
              StringModifier.SENTENCE_CASE
            )} without its ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)}!`
          )
        );
        return res;
      })
      .then(onCompletion)
      .catch((err) => {
        const updatedErrMessage = findAndReplaceString(
          err.message,
          `${modifyString(objectivesName, StringModifier.UPPERCASE_ARTICLE)} ${modifyString(
            objectivesName,
            StringModifier.SENTENCE_CASE
          )}`,
          'An objective'
        );
        dispatch(showErrorSnackbar(updatedErrMessage));
      });
  };

  const handleRadioChange = (event) => {
    setIncludeObjectiveTree(event.target.value);
  };

  const targetTimePeriodsList = timePeriodsList.filter(
    (timeBound) => getTimePeriodIdString(timeBound) !== getTimePeriodIdString(sourceObjective.timeBoundDetails)
  );

  // const handleTagSubTypeChange = useCallback(
  //   objectiveLevel => {
  //     setCurrentObjective(oldTag => ({ ...oldTag, objectiveLevel: objectiveLevel.value }));
  //   },
  //   [setCurrentObjective]
  // );

  return (
    <ModalWrapperContainer>
      <Typography6 weight={400} bottomGutter={1}>
        Clone {objectivesName}
      </Typography6>

      <Typography4 customColor={theme.palette.text.dark1}>
        Copy {modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)} and{' '}
        {modifyString(objectivesName, StringModifier.PLURAL_SENTENCE_CASE)} in another time period
      </Typography4>
      <Divider1 topGutter={3} bottomGutter={2} />

      <StyledSpaceBetweenContainer>
        <Typography4>Select time period</Typography4>
        <Width160Container>
          <TimePeriodDropDown
            timePeriodData={timePeriodData}
            selectedTimeBoundPeriod={selectedTargetTimeBoundPeriod}
            setSelectedTimeBoundPeriod={setSelectedTargetTimeBoundPeriod}
          />
        </Width160Container>
      </StyledSpaceBetweenContainer>
      {/* <Divider1 topGutter={2} bottomGutter={2} /> */}

      {/* <StyledSpaceBetweenContainer>
        <Typography4>Objective level</Typography4>
        <Width160Container>
          <StyledSelectDropdown
            isEditing={true}
            options={OBJECTIVE_LEVEL_TAGS}
            onChange={handleTagSubTypeChange}
            value={
              OBJECTIVE_LEVEL_TAGS.filter(opt => opt.value === currentObjective.objectiveLevel)[0]
            }
            classNamePrefix="driver-select"
            placeholder="Select Type"
            components={{ Option: DriverOption, SingleValue: DriverSingleValue }}
          />
        </Width160Container>
      </StyledSpaceBetweenContainer> */}

      <Divider1 topGutter={2} bottomGutter={2} />
      <StyledSpaceBetweenContainer>
        <Typography4>Clone with {moduleName} tree</Typography4>

        <Width160Container>
          <StyledRadioGroup
            row
            aria-label="radio-selection"
            name="prompt"
            value={includeObjectiveTree}
            onChange={handleRadioChange}
            color="primary"
            sx={{
              '& .MuiSvgIcon-root': {
                fontSize: 28,
              },
            }}
          >
            <StyledFormControlLabel
              value="yes"
              control={<Radio color="primary" size="small" />}
              label="Yes"
              labelPlacement="End"
            />
            <StyledFormControlLabel
              value="no"
              control={<Radio color="primary" size="small" />}
              label="No"
              labelPlacement="End"
            />
          </StyledRadioGroup>
        </Width160Container>
      </StyledSpaceBetweenContainer>
      <Divider1 topGutter={2} bottomGutter={2} />

      <ActionButtons
        onSave={handleCloneObjective}
        onCancel={onDiscardHandler}
        primaryActionText="Clone"
        secondaryActionText="Cancel"
      />
    </ModalWrapperContainer>
  );
};

export const DeleteObjectiveModalGoalsRevamp = ({ onCompletion, objectiveTimeBoundId, tag }) => {
  // const [selectedValue, setSelectedValue] = useState('yes');
  const dispatch = useDispatch();
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const onDeleteHandler = () => {
    deleteObjective({ objectiveTimeBoundId, archiveObjectiveTree: true, isDeleteRevamp: true })
      .then(() => onCompletion(true))
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message));
        onCompletion(false);
      });
  };
  const onDiscardHandler = () => onCompletion(false);

  return (
    <ModalWrapperContainer>
      <VerticallyAlignedFlexContainer style={{ marginBottom: '1.25rem' }}>
        <Typography6 weight={600}>Delete {objectivesName}</Typography6>

        <CrossIcon
          size="fixed16px"
          style={{ marginLeft: 'auto' }}
          onClick={onDiscardHandler}
          color={theme.palette.icons.mediumGrey}
        />
      </VerticallyAlignedFlexContainer>

      <StyledSpaceBetweenContainer style={{ marginBottom: '1.25rem' }}>
        <Typography14>
          Deleting this {modifyString(objectivesName, StringModifier.SENTENCE_CASE)} will also delete all the{' '}
          {modifyString(objectivesName, StringModifier.PLURAL_SENTENCE_CASE)} and{' '}
          {modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)} aligned to it
        </Typography14>
      </StyledSpaceBetweenContainer>
      <Divider1 topGutter={2} bottomGutter={2} style={{ marginLeft: '-1.25rem', marginRight: '-1.25rem' }} />

      <VerticallyAlignedFlexContainer style={{ padding: '1rem 0' }}>
        <Typography13 onClick={onDiscardHandler} style={{ width: '8rem', marginRight: 'auto' }} weight={600}>
          Cancel
        </Typography13>
        <Button
          redOutline
          size="large"
          rightGutter={3}
          onClick={onDeleteHandler}
          style={{ width: '6rem', padding: 0, fontWeight: '600', height: '2rem' }}
        >
          Delete
        </Button>
        <Button
          size="large"
          blue
          rightGutter={3}
          onClick={() => {
            dispatch(
              openRealignModalAction({
                objectiveId: tag.objectiveId,
                objectiveName: tag.displayName,
                selectedTimeBoundPeriod: tag.timeBoundDetails,
                selectedObjective: {
                  timeBoundDetails: tag.timeBoundDetails,
                  objectiveId: tag.objectiveId,
                },
                isDeleteRealign: true,
                realignId: tag.objectiveTimeBoundId,
                onSuccess: () => {
                  onCompletion(true);
                },
              })
            );
          }}
          style={{
            width: '6rem',
            padding: 0,
            fontWeight: '600',
            height: '2rem',
            marginLeft: '1rem',
          }}
        >
          Realign
        </Button>
        {/* <ActionButtons
          onSave={onDeleteHandler}
          onCancel={onDiscardHandler}
          primaryActionText="Delete"
          secondaryActionText="Cancel"
        /> */}
      </VerticallyAlignedFlexContainer>
    </ModalWrapperContainer>
  );
};

export const ObjectiveTreeCreation = ({ onCompletion, currentObjective }) => {
  const [selectedValue, setSelectedValue] = useState(OBJECTIVE_GOAL_CREATION_MODAL_CHOICES[1]);
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);

  const { objectiveCreationAccess } = useSelector(selectOrgData);

  const showObjectiveOption = () => {
    if (objectiveCreationAccess === OBJECTIVE_ACCESS_PERMISSIONS[0].value) {
      return checkUserIsGoalAdmin();
    }
    if (objectiveCreationAccess === OBJECTIVE_ACCESS_PERMISSIONS[1].value) {
      return checkUserIsGoalAdmin() || loggedInEmployee?.isHRISManager;
    }

    return false;
  };

  const onCreateHandler = () => {
    onCompletion(selectedValue);
  };
  const onDiscardHandler = () => onCompletion(OBJECTIVE_GOAL_CREATION_MODAL_CHOICES[2]);

  const handleChange = (event) => {
    setSelectedValue(event.target.value);
  };

  return (
    <ModalWrapperContainer>
      <Typography6 weight={400} bottomGutter={1} style={{ fontSize: '20px' }}>
        Create New
      </Typography6>
      <Typography4 color={theme.palette.text.dark1}>
        Your {modifyString(objectivesName, StringModifier.SENTENCE_CASE)}/
        {modifyString(keyResultsName, StringModifier.SENTENCE_CASE)} will be created under{' '}
        <b>{currentObjective.displayName}</b>
      </Typography4>
      <Divider1 topGutter={3} bottomGutter={2} />

      <StyledRadioGroup aria-label="radio-selection" name="prompt" value={selectedValue} onChange={handleChange} dir>
        {showObjectiveOption() && (
          <StyledFormControlLabel
            value={OBJECTIVE_GOAL_CREATION_MODAL_CHOICES[1]}
            control={<Radio color="primary" size="small" />}
            label={<Typography4 customColor={theme.palette.text.dark1}>{objectivesName}</Typography4>}
            labelPlacement="End"
          />
        )}
        <StyledFormControlLabel
          value={OBJECTIVE_GOAL_CREATION_MODAL_CHOICES[0]}
          control={<Radio color="primary" size="small" />}
          label={<Typography4 customColor={theme.palette.text.dark1}>{keyResultsName}</Typography4>}
          labelPlacement="End"
        />
      </StyledRadioGroup>
      <Divider1 topGutter={2} bottomGutter={2} />

      <ActionButtons
        onSave={onCreateHandler}
        onCancel={onDiscardHandler}
        primaryActionText="Create"
        secondaryActionText="Cancel"
      />
    </ModalWrapperContainer>
  );
};

export const DeleteKRModalGoalsRevamp = ({ onDelete }) => {
  // const [selectedValue, setSelectedValue] = useState('yes');
  const dispatch = useDispatch();
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const onDeleteHandler = () => {
    onDelete();
  };
  const onDiscardHandler = () => {
    dispatch(closeKRPromptModal());
  };

  return (
    <ModalWrapperContainer>
      <VerticallyAlignedFlexContainer style={{ marginBottom: '1.25rem' }}>
        <Typography6 weight={600}>Delete {keyResultsName}</Typography6>

        <CrossIcon
          size="fixed16px"
          style={{ marginLeft: 'auto' }}
          onClick={onDiscardHandler}
          color={theme.palette.icons.mediumGrey}
        />
      </VerticallyAlignedFlexContainer>

      <Flex direction="column" rowGap={6} style={{ marginBottom: '1.25rem' }}>
        <Typography14>
          Deleting this {modifyString(keyResultsName, StringModifier.SENTENCE_CASE)} will remove it for other owners and
          collaborators. <br />
          Your Performance Review answers, 1:1s notes, and Initiatives for this{' '}
          {modifyString(keyResultsName, StringModifier.SENTENCE_CASE)} will also be impacted.
        </Typography14>

        <Typography14>You can remove yourself as an owner or collaborator instead of deleting it.</Typography14>
      </Flex>
      <Divider1 topGutter={2} bottomGutter={2} style={{ marginLeft: '-1.25rem', marginRight: '-1.25rem' }} />

      <Flex justifyContent="flex-end" alignItems="center" style={{ padding: '1rem 0' }}>
        <Button
          redOutline
          size="large"
          onClick={onDeleteHandler}
          style={{
            width: '6rem',
            padding: 0,
            fontWeight: '600',
            height: '2rem',
            marginRight: '1rem',
          }}
        >
          Delete
        </Button>
        <Button
          onClick={onDiscardHandler}
          // style={{ width: '8rem', marginRight: 'auto' }}
          weight={600}
          size="large"
          blue
          style={{
            width: '6rem',
            padding: 0,
            fontWeight: '600',
            height: '2rem',
          }}
        >
          Go Back
        </Button>

        {/* <ActionButtons
          onSave={onDeleteHandler}
          onCancel={onDiscardHandler}
          primaryActionText="Delete"
          secondaryActionText="Cancel"
        /> */}
      </Flex>
    </ModalWrapperContainer>
  );
};
